/*
 * @providesModule BalanceBarV3
 *
 */
import {IAnimatedBurstShowerProps} from '@skillz/ui';
import {
  CURRENCY_BAR_CASH,
  CURRENCY_BAR_CHAT,
  CURRENCY_BAR_TICKETZ,
  CURRENCY_BAR_Z,
  FTUE_LOBBY_WALLET_VIEW,
  Z_CASH_CLICK,
  WALLET_BALANCE_ICON_CLICK,
  ADD_CASH_CTA_CLICK,
  WALLET_TT_TRANSACTIONS_CLICK,
  WALLET_TT_WITHDRAW_CLICK,
} from 'AWSConstants';
import AWSManager from 'AWSManager';
import {IAnimatedShowerProps} from 'AnimatedShower';
import AssetHelper from 'AssetHelper';
import styles from 'BalanceBarV3Style';
import ClientPropertyConstants from 'ClientPropertyConstants';
import GlobalEventEmitter from 'GlobalEventEmitter';
import constants from 'HomeScreenConstants';
import I18n from 'INTL';
import NativeNavigation from 'NativeNavigation';
import NavigationManager from 'NavigationManager';
import Optional from 'Optional';
import SKZConstants from 'SKZConstants';
import Sizes from 'Sizes';
import {
  MedalCountingLabel,
  TicketzCountingLabel,
  AddCashButton,
  WalletCountingLabel,
  ZCountingLabelV2,
} from 'SkillzCountingLabels';
import SkillzSVG from 'SkillzSVG';
import SkillzImage from 'SkillzImage';
import SkillzStyle, {chatIconSvg} from 'SkillzStyle';
import _ from 'lodash';
import React from 'react';
import {
  Animated,
  EmitterSubscription,
  ImageSourcePropType,
  LayoutChangeEvent,
  NativeModules,
  Platform,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
  findNodeHandle,
  Pressable,
} from 'react-native';
import {pickTextColorBasedOnBgColor} from '@skillz-rn/utils';
import {TooltipView} from '@skillz/ui';
import AccountBalanceScreen from 'AccountBalanceScreen';
import BalanceSplitComponent from 'BalanceSplitComponent';
import {SKZUserInterface} from 'SKZUser';
import FTUEController from 'FTUEController';
import CurrentUserStore from 'CurrentUserStore';
import WithdrawalManager from 'WithdrawalManager';
import CountingLabel from 'CountingLabel';
import Label from 'Label';
import SkillzRouter from 'SkillzRouter';
import SkillzModule from 'SkillzModule';
import {TelemetryEventName} from 'TelemetryConstants';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {FTUETooptipV2Context} from 'FTUETooltipV2';
import SkillzSDK from 'SkillzSDK';
import ScreenNames from 'ScreenNames';

const RCTUIManager = NativeModules.UIManager;

const TEST_IDS = {
  BALANCE_BAR: 'balance_bar',
  CHAT_BADGE: 'chat_badge',
  CHAT_BUTTON: 'chat_button',
};

const CurrencyTypes = SKZConstants.CurrencyTypes;

type SKZCurrencyTypes = 'CASH' | 'TICKETZ' | 'Z' | 'MEDAL';

type ShowerAnimationConfigOptions = {
  x: number;
  y: number;
  iconCount: number | string;
  currencyCount: number | string;
  itemSize?: number;
  screenName?: string;
};

export type chatSpotlightProps = {
  spotlightCornerRadiusChat: number;
  spotlightRefChat: number;
};

interface IViewStylePlusTextColor extends ViewStyle {
  color?: string;
}

type Props = {
  shouldAnimate?: boolean;
  forceAnimation?: boolean;
  disableNewFTUETooltip?: boolean;
  animationCurve?: string;
  animationDuration?: number;
  animationPrecisions?: {z: number; cash: number; ticketz: number};
  cashBalance: number;
  zBalance: number;
  ticketzBalance: number;
  medalBalance: number;
  currencyLayout?: (arg1: {
    nativeEvent: {
      layout: {
        x: number;
        y: number;
        width: number;
        height: number;
      };
    };
  }) => void;
  borderType: 'surround' | 'divider' | 'none';
  borderColor?: string;
  currencyOrder: string[];
  style?: IViewStylePlusTextColor;
  isTouchable?: boolean;
  disableEvents?: boolean;
  identifier?: string;
  showRightSideIcon?: boolean;
  showChatBadge?: boolean;
  overrideTheming?: boolean;
  lightThemeOverride?: boolean;
  darkThemeOverride?: boolean;
  /** this function when defined is a callback method for when the chat icon is available for education */
  onEducationChatSpotlightUpdated: (props: chatSpotlightProps) => {};
  /** This is a ref that gets assigned to the chat icon for education */
  educationChatRef: React.RefObject<View> | undefined;
  zCashRef: React.RefObject<View> | undefined;
  /** This is a ref for the Add Cash button for FTUE V2 tooltips */
  addCashButtonRef?: React.RefObject<View>;
  showAddCashButton?: boolean;
  onClickZCurrency?: () => void;
  onClickTicketzCurrency?: () => void;
  onClickCashCurrency?: () => void;
  /** (Optional) will be called whenever the wallet tooltip is dismissed.
   * Used by Home screen to show other available tooltips after dismissing the wallet overlay
   */
  onDismissWalletTooltip?: () => void;
  isContentCentered?: boolean;
  isZCashCollapsed?: boolean;
  isNewHeaderEnabled?: boolean;
  userInfo?: SKZUserInterface;
  screenName?: string;
};

type State = {
  showZTooltip: boolean;
  showNewWalletTooltip: boolean;
  newFTUETooltip: boolean;
};

export const getIconURL = (): string => {
  const currentCurrency = I18n.currentCurrency();
  switch (currentCurrency) {
    case 'INR':
      return AssetHelper.pathForAssetNamed('ic_cash_skz_inr.png', false);
    default:
      return AssetHelper.pathForAssetNamed('ic_cash_usd_background.png', false);
  }
};

export const requestBurstShowerAnimation = (
  config: Array<{currencyType: SKZCurrencyTypes; options: ShowerAnimationConfigOptions}>,
  cashRef: React.Component<any, any>,
  zRef: React.Component<any, any>,
  ticketRef: React.Component<any, any>
) => {
  const promises: Promise<IAnimatedBurstShowerProps>[] = [];
  let currencyRef: React.Component<any, any> | null = null;
  _.forEach(
    config,
    (configItem: {currencyType: SKZCurrencyTypes; options: ShowerAnimationConfigOptions}) => {
      let currencyImg: ImageSourcePropType | undefined = undefined;
      switch (configItem.currencyType) {
        case CurrencyTypes.CASH: {
          currencyRef = cashRef;
          currencyImg = {uri: getIconURL()};
          break;
        }
        case CurrencyTypes.Z: {
          currencyImg = {
            uri: AssetHelper.pathForAssetNamed(
              SkillzStyle.currencyValues.practiceCurrencyFullIconLargeURI
            ),
          };
          currencyRef = zRef;
          break;
        }
        case CurrencyTypes.TICKETZ: {
          currencyImg = {uri: AssetHelper.pathForAssetNamed('ic_ticketz_background.png', false)};
          currencyRef = ticketRef;
          break;
        }
      }
      const promise = new Promise(
        (resolve: (prop: IAnimatedBurstShowerProps) => void, _reject: (error: Error) => void) => {
          const node = findNodeHandle(currencyRef);
          const currencyCap = configItem.currencyType == CurrencyTypes.CASH ? 7 : 9;
          if (!node) {
            console.warn('BalanceBarV3: Shower node undefined');
          }
          const getCurrencyCountNumber = (count: number | string): number => {
            if (typeof count === 'string') {
              return parseInt(count);
            }
            return count;
          };

          RCTUIManager.measure(
            node,
            (
              _x: number,
              _y: number,
              width: number,
              _height: number,
              pagePointX: number,
              pagePointY: number
            ) => {
              let endXOffset = 5;
              if (
                configItem.currencyType == CurrencyTypes.CASH &&
                Platform.OS == 'android' &&
                !ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()
              ) {
                endXOffset = 10;
              }
              const widthXFallback = Number(pagePointX) + 7;
              const delayVCap = Number(configItem.options.iconCount) < 8 ? 75 : 50;
              const decrementYpoint = ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()
                ? 4
                : 1;
              const scaleItems = ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()
                ? [0.4, 1.5, 1.1, 1]
                : [0.4, 1.5, 1, 0.7];
              const startingX = configItem.options.x;
              const startingY = configItem.options.y;
              const endingX = width !== 0 ? Math.floor(pagePointX) - endXOffset : widthXFallback;
              const endingY = Math.floor(pagePointY) - decrementYpoint;
              const controlX = Math.max(startingX, endingX) + 100;
              const params = {
                icon: currencyImg,
                startingPoint: {x: startingX, y: startingY},
                endingPoint: {
                  x: endingX,
                  y: endingY,
                },
                iconCount:
                  getCurrencyCountNumber(configItem.options.iconCount) < currencyCap
                    ? getCurrencyCountNumber(configItem.options.iconCount)
                    : currencyCap,
                animatedValue: {
                  startingVariant: {x: 30, y: 30},
                  delayV: delayVCap,
                  duration: 600,
                  delay: 500,
                  endDelay: 170,
                  itemSize: configItem?.options?.itemSize || 33,
                  scaleItems: scaleItems,
                  opacity: [0, 1],
                  control: {x: controlX, y: startingY},
                  fadeOut: false,
                  scaleUp: false,
                },
              };
              resolve(params);
            }
          );
        }
      );
      promises.push(promise);
    }
  );
  Promise.all(promises).then((showerArray: IAnimatedBurstShowerProps[]) => {
    GlobalEventEmitter.emit('commitBurstShowerAnimations', showerArray);
  });
};

export const requestShowerAnimation = (
  config: Array<{currencyType: SKZCurrencyTypes; options: ShowerAnimationConfigOptions}>,
  cashRef: React.Component<any, any>,
  zRef: React.Component<any, any>,
  ticketRef: React.Component<any, any>
) => {
  let currencyRef: React.Component<any, any> | null = null;
  const promises: Promise<IAnimatedShowerProps>[] = [];
  _.forEach(
    config,
    (configItem: {currencyType: SKZCurrencyTypes; options: ShowerAnimationConfigOptions}) => {
      let currencyImg: string | undefined = undefined;
      switch (configItem.currencyType) {
        case CurrencyTypes.CASH: {
          currencyRef = cashRef;
          currencyImg = getIconURL();
          break;
        }
        case CurrencyTypes.Z: {
          currencyImg = SkillzStyle.currencyValues.practiceCurrencyFullIconLargeURI;
          currencyRef = zRef;
          break;
        }
        case CurrencyTypes.TICKETZ: {
          currencyImg = AssetHelper.pathForAssetNamed('ic_ticketz_background.png', false);
          currencyRef = ticketRef;
          break;
        }
      }
      const promise = new Promise(
        (resolve: (prop: IAnimatedShowerProps) => void, _reject: (error: Error) => void) => {
          const node = findNodeHandle(currencyRef);
          const currencyCap = configItem.currencyType == CurrencyTypes.CASH ? 200 : 250;
          if (!node) {
            console.warn('BalanceBarV3: Burst shower node undefined');
          }

          const getCurrencyCountNumber = (count: number | string): number => {
            if (typeof count === 'string') {
              return parseInt(count);
            }
            return count;
          };

          RCTUIManager.measure(
            node,
            (
              _x: number,
              _y: number,
              width: number,
              _height: number,
              pagePointX: number,
              pagePointY: number
            ) => {
              let endXOffset = 5;
              if (
                configItem.currencyType == CurrencyTypes.CASH &&
                Platform.OS == 'android' &&
                !ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()
              ) {
                endXOffset = 10;
              }
              const widthXFallback = Number(pagePointX) + 7;
              const durationVCap = Number(configItem.options.currencyCount) < 50 ? 200 : 50;
              const decrementYpoint = ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()
                ? 4
                : 1;
              const scaleItems = ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()
                ? [0.4, 0.7, 1.1, 1]
                : [0.4, 0.7, 1, 0.7];
              const params = {
                icon: currencyImg,
                startingPoint: {x: configItem.options.x, y: configItem.options.y},
                endingPoint: {
                  x: width !== 0 ? Math.floor(pagePointX) - endXOffset : widthXFallback,
                  y: Math.floor(pagePointY) - decrementYpoint,
                },
                animationCount:
                  getCurrencyCountNumber(configItem.options.currencyCount) < currencyCap
                    ? getCurrencyCountNumber(configItem.options.currencyCount)
                    : currencyCap,
                animatedValue: {
                  startingVariant: {x: 20, y: 20},
                  durationV: durationVCap,
                  duration: 700,
                  delay: 300,
                  itemSize: 30,
                  scaleItems,
                  opacity: [0.5, 1, 1, 0],
                },
              };
              resolve(params);
            }
          );
        }
      );
      promises.push(promise);
    }
  );
  Promise.all(promises).then((showerArray: IAnimatedShowerProps[]) => {
    GlobalEventEmitter.emit('commitShowerAnimations', showerArray);
  });
};

export default class BalanceBar extends React.Component<Props, State> {
  flattenedStyle: ViewStyle;
  backgroundColor: any;
  animation: Animated.Value;
  scale: Animated.AnimatedInterpolation<number>;
  currencyFlyingListener: any;
  currencyBurstFlyingListener: EmitterSubscription | undefined;
  tournamentHighlightListener: any; // Listener for FTUE template highlight dismissal
  cashRef: any;
  zCoinRef: any;
  ticketzRef: any;
  medalRef: any;

  lightThemeBorder: ViewStyle;
  darkThemeBorder: ViewStyle;

  static defaultProps = {
    shouldAnimate: true,
    borderType: 'none',
    currencyOrder: [CurrencyTypes.Z, CurrencyTypes.TICKETZ, CurrencyTypes.CASH],
    style: {height: constants.getBalanceBarHeight()},
    isTouchable: true,
    animationPrecisions: {z: 0, cash: 2, ticketz: 0},
    animationCurve: 'ease-in',
    animationDuration: 2000,
    identifier: '',
    forceAnimation: false,
    showRightSideIcon: false,
    showChatBadge: false,
    lightThemeOverride: false,
    darkThemeOverride: false,
    screenName: undefined,
  };

  constructor(props: Props) {
    super(props);

    this.flattenedStyle = StyleSheet.flatten([styles.balanceView, this.props.style]);
    this.backgroundColor = this.flattenedStyle.backgroundColor;
    this.animation = new Animated.Value(0);
    this.scale = this.animation.interpolate({
      inputRange: [0, 1],
      outputRange: [1, 1.15],
    });
    this.lightThemeBorder = this.props.lightThemeOverride ? styles.lightThemeOverride : {};
    this.darkThemeBorder = this.props.darkThemeOverride ? styles.darkThemeOverride : {};
    this.state = {
      showZTooltip: false,
      showNewWalletTooltip: false,
      newFTUETooltip: false,
    };
  }

  componentDidMount() {
    if (!this.props.disableEvents) {
      this.currencyFlyingListener = GlobalEventEmitter.addListener(
        'requestShowerAnimation',
        (
          config: Array<{currencyType: SKZCurrencyTypes; options: ShowerAnimationConfigOptions}>
        ) => {
          // @ts-ignore: Methods for refs not implemented
          requestShowerAnimation(config, this.cashRef, this.zCoinRef, this.ticketzRef);
        }
      );
      // @ts-ignore: Custom event not in GlobalEventEmitter types
      this.currencyBurstFlyingListener = GlobalEventEmitter.addListener(
        'requestBurstShowerAnimation',
        // @ts-ignore: Custom event parameter types
        (
          config: Array<{currencyType: SKZCurrencyTypes; options: ShowerAnimationConfigOptions}>
        ) => {
          // Check if any config item has screenName === 'result_screen'
          const isResultScreen = config?.some(
            (item) => item.options.screenName === 'result_screen'
          );

          // Early return: If this is a result_screen animation but this component is not result_screen, ignore it
          if (isResultScreen && this.props.screenName !== 'result_screen') {
            return;
          }

          // Early return: If this is not a result_screen animation but this component is result_screen, ignore it
          if (!isResultScreen && this.props.screenName === 'result_screen') {
            return;
          }

          // @ts-ignore: Methods for refs not implemented
          if (isResultScreen && this.props.screenName === 'result_screen') {
            // Result screen: use only result_screen specific configs
            const filteredConfig = config.filter(
              (item) => item.options.screenName === 'result_screen'
            );

            requestBurstShowerAnimation(
              filteredConfig,
              this.cashRef,
              this.zCoinRef,
              this.ticketzRef
            );
          } else if (!isResultScreen && this.props.screenName !== 'result_screen') {
            // Home screen: use the entire config when no result_screen specific items
            requestBurstShowerAnimation(config, this.cashRef, this.zCoinRef, this.ticketzRef);
          }
        }
      );
    }

    const isShowingMatchTutorial =
      !FTUEController.hasSeenMatchSelectTutorial() || !FTUEController.hasSeenMatchStreamTutorial();

    // 🎯 CRITICAL: Check if FTUE V2 template highlighting is active
    // If template highlighting is active, defer wallet tooltip to avoid conflicting overlays
    const isFTUEV2Enabled = ClientPropertyConstants.getFTUEV2FeatureEnabled();
    const isTemplateHighlightActive = isFTUEV2Enabled; // Template highlight can be triggered for first-time users

    if (
      !FTUEController.hasSeenHomeScreenWalletTooltip() &&
      !isShowingMatchTutorial &&
      !isTemplateHighlightActive
    ) {
      this.setState({
        ...this.state,
        newFTUETooltip: !FTUEController.hasSeenHomeScreenWalletTooltip(),
      });
      AWSManager.trackEvent(FTUE_LOBBY_WALLET_VIEW);
    }

    // 🎯 FTUE V2 TEMPLATE HIGHLIGHT INTEGRATION:
    // Listen for highlight dismissal to show wallet tooltip after template highlight is closed
    if (isFTUEV2Enabled && !FTUEController.hasSeenHomeScreenWalletTooltip()) {
      this.tournamentHighlightListener = GlobalEventEmitter.addListener(
        'HideTournamentHighlight',
        () => {
          // Show wallet tooltip after a small delay to avoid immediate overlap
          setTimeout(() => {
            if (!FTUEController.hasSeenHomeScreenWalletTooltip() && !this.state.newFTUETooltip) {
              this.setState({
                ...this.state,
                newFTUETooltip: true,
              });
              AWSManager.trackEvent(FTUE_LOBBY_WALLET_VIEW);
            }
          }, 500); // 500ms delay for smooth transition
        }
      );
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.showChatBadge != this.props.showChatBadge && this.props.showChatBadge) {
      this._animateChatButton();
    }
  }

  componentWillUnmount() {
    if (!this.props.disableEvents) {
      this.currencyFlyingListener?.remove();
      this.currencyFlyingListener = null;

      // Clean up tournament highlight listener
      if (this.tournamentHighlightListener) {
        this.tournamentHighlightListener.remove();
        this.tournamentHighlightListener = null;
      }
      this.currencyBurstFlyingListener?.remove();
      this.currencyBurstFlyingListener = null;
    }
  }

  _animateChatButton() {
    Animated.sequence([
      Animated.timing(this.animation, {
        toValue: 1,
        useNativeDriver: false,
        duration: 300,
      }),
      Animated.timing(this.animation, {
        toValue: 0,
        useNativeDriver: false,
        duration: 300,
      }),
    ]).start();
  }

  _updateBalanceViewLayout(e: any) {
    e.persist?.();

    if (!this.props.currencyLayout) {
      return;
    }

    const balanceLayout = e.nativeEvent.layout;
    balanceLayout.rootX = e.nativeEvent.layout.x;
    balanceLayout.rootY = 0;
    if (Sizes.isLandscape()) {
      if (Sizes.isTablet()) {
        balanceLayout.width = Sizes.getUIWidth();
        balanceLayout.height = 0;
      } else {
        balanceLayout.rootY = e.nativeEvent.layout.y + constants.MENU_HEIGHT;
        balanceLayout.width = 0;
        balanceLayout.height = e.nativeEvent.layout.height;
        balanceLayout.rootX = 0;
      }
    } else {
      balanceLayout.height = 0;
    }
    this.props.currencyLayout(balanceLayout);
  }

  _onChatButtonPress = () => {
    GlobalEventEmitter.emit('LaunchChatLobby');
    AWSManager.trackEvent(CURRENCY_BAR_CHAT);
  };

  _getTextStyle(): TextStyle {
    if (
      (this.props.lightThemeOverride || this.props.darkThemeOverride) &&
      this.props.style?.color
    ) {
      return {color: this.props.style.color};
    }
    return {};
  }

  _renderBalance(
    key: string,
    accessibilityLabel: string,
    testID: string,
    countingLabel: React.ReactElement,
    _index: number,
    containerStyle?: {}
  ): React.ReactElement {
    const borderStyle = styles['balanceBorder_' + this.props.borderType.toLowerCase()];
    const dividerColor = this.props.borderColor ? {borderColor: this.props.borderColor} : {};
    return (
      <View
        style={[styles.balanceContainer, containerStyle, borderStyle, dividerColor]}
        accessible={true}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole={'button'}
        testID={testID}
        key={key}
      >
        <View style={styles.counterView}>
          <View style={styles.countingLabelContainer}>{countingLabel}</View>
        </View>
      </View>
    );
  }

  _renderTicketzBalance(index: number): React.ReactElement {
    let onPressFunction: null | (() => void) | (() => {}) = null;
    if (this.props.isTouchable) {
      onPressFunction = () => {
        NavigationManager.displayTicketz();
        this._removeAllTooltip();
        const ticketzBalance = JSON.stringify({
          ticketz_balance: this.props.userInfo?.ticketz?.balance,
        });
        AWSManager.trackEvent(FTUE_LOBBY_WALLET_VIEW, {
          result: ticketzBalance,
        });
        trackTelemetryEvent({
          event: TelemetryEventName.TICKETZ_STORE_LOAD,
          includeUserWalletDetails: true,
        });
      };
    }

    return this._renderBalance(
      'ticketz',
      I18n.message('Ticketz balance {0}', this.props.ticketzBalance),
      'ticketz_balance',
      <TicketzCountingLabel
        style={[styles.countingLabel, this._getTextStyle()]}
        containerStyle={
          this.props.style?.backgroundColor
            ? [
                this.lightThemeBorder,
                this.darkThemeBorder,
                {backgroundColor: this.props.style?.backgroundColor},
              ]
            : [this.lightThemeBorder, this.darkThemeBorder]
        }
        ticketzLayout={(_e: any) => {
          /*RN Measure Workaround for Android*/
        }}
        value={this.props.ticketzBalance}
        animationCurve={this.props.animationCurve}
        animationDuration={this.props.animationDuration}
        animationPrecision={this.props.animationPrecisions?.ticketz}
        ticketzRef={(ref: any) => {
          this.ticketzRef = ref;
        }}
        shouldAnimate={this.props.shouldAnimate}
        identifier={this.props.identifier}
        forceAnimation={this.props.forceAnimation}
        onPressFunc={onPressFunction}
      />,
      index
    );
  }

  _removeAllTooltip() {
    this.setState({
      showZTooltip: false,
      showNewWalletTooltip: false,
      newFTUETooltip: false,
    });
  }

  _renderZBalance(index: number): React.ReactElement {
    let onPressFunction: null | (() => void) | (() => {}) = null;
    if (this.props.isTouchable) {
      onPressFunction = () => {
        if (!this.props.onClickZCurrency) {
          this.setState({
            showZTooltip: true,
            showNewWalletTooltip: false,
            newFTUETooltip: false,
          });
          const zBalanceString = JSON.stringify({z_balance: this.props.userInfo?.z_balance});
          AWSManager.trackEvent(Z_CASH_CLICK, {
            result: zBalanceString,
          });
          trackTelemetryEvent({
            event: TelemetryEventName.LOBBY_Z_ICON_CLICK,
            payload: {
              source: this.props.screenName ?? '',
            },
            includeUserWalletDetails: true,
          });
        } else {
          this.props.onClickZCurrency?.();
        }
      };
    }

    const zTagComponent = (
      <ZCountingLabelV2
        style={[styles.countingLabel, this._getTextStyle()]}
        containerStyle={
          this.props.style?.backgroundColor
            ? [
                this.lightThemeBorder,
                this.darkThemeBorder,
                {backgroundColor: this.props.style?.backgroundColor},
              ]
            : [this.lightThemeBorder, this.darkThemeBorder]
        }
        coinLayout={(_e: any) => {
          /*RN Measure Workaround for Android*/
        }}
        coinRef={(ref: any) => {
          this.zCoinRef = ref;
        }}
        value={this.props.zBalance}
        animationCurve={this.props.animationCurve}
        animationDuration={this.props.animationDuration}
        animationPrecision={this.props.animationPrecisions?.z}
        shouldAnimate={this.props.shouldAnimate}
        identifier={this.props.identifier}
        forceAnimation={this.props.forceAnimation}
        isCollapsible={this.props.isZCashCollapsed}
        onPressFunc={onPressFunction}
      />
    );

    return this._renderBalance(
      'z',
      I18n.message('Z balance {0}', this.props.zBalance),
      'z_balance',
      zTagComponent,
      index
    );
  }

  _renderCashBalance(index: number): React.ReactElement | boolean {
    let onPressFunction: null | (() => void) | (() => {}) = null;
    if (this.props.isTouchable) {
      onPressFunction = () => {
        if (!this.props.onClickCashCurrency) {
          this.setState({showNewWalletTooltip: true, showZTooltip: false, newFTUETooltip: false});
          const userInfoBalance = JSON.stringify({
            total_balance: this.props.userInfo?.cash_balance,
            real_cash_balance: this.props.userInfo?.real_cash_balance,
            bonus_cash_balance: this.props.userInfo?.bonus_balance,
          });
          AWSManager.trackEvent(WALLET_BALANCE_ICON_CLICK, {result: userInfoBalance});
          trackTelemetryEvent({
            event: TelemetryEventName.LOBBY_WALLET_ICON_CLICK,
            includeUserWalletDetails: true,
          });
        } else {
          this.props.onClickCashCurrency();
        }
      };
    }
    if (ClientPropertyConstants.getIsVirtualCurrencyOnly()) {
      return false;
    }
    const newWalletLabelComponent = (
      <WalletCountingLabel
        style={[styles.countingLabel, this._getTextStyle()]}
        containerStyle={
          this.props.style?.backgroundColor
            ? [
                this.lightThemeBorder,
                this.darkThemeBorder,
                {backgroundColor: this.props.style?.backgroundColor},
              ]
            : [this.lightThemeBorder, this.darkThemeBorder]
        }
        cashLayout={(_e: any) => {
          /*RN Measure Workaround for Android*/
        }}
        cashRef={(ref: any) => {
          this.cashRef = ref;
        }}
        value={this.props.cashBalance}
        animationCurve={this.props.animationCurve}
        animationDuration={this.props.animationDuration}
        animationPrecision={this.props.animationPrecisions?.cash}
        shouldAnimate={this.props.shouldAnimate}
        identifier={this.props.identifier}
        forceAnimation={this.props.forceAnimation}
        onPressFunc={onPressFunction}
      />
    );
    return this._renderBalance(
      'cash',
      I18n.message('Cash balance {0}', I18n.getCurrencyString(this.props.cashBalance, '$')),
      'cash_balance',
      newWalletLabelComponent,
      index
    );
  }

  _renderAddCashButton(index: number, tooltipV2?: any) {
    const onPressFunction = () => {
      NavigationManager.popEverythingToRoot();
      this._removeAllTooltip();
      GlobalEventEmitter.emit('switchToTab', 'STORE');
      const userInfoBalance = JSON.stringify({
        total_balance: this.props.userInfo?.cash_balance,
        real_cash_balance: this.props.userInfo?.real_cash_balance,
        bonus_cash_balance: this.props.userInfo?.bonus_balance,
      });
      AWSManager.trackEvent(ADD_CASH_CTA_CLICK, {result: userInfoBalance});
    };
    return this._renderBalance(
      'addcash',
      I18n.message('Add Cash'),
      'add_cash',
      <View
        onLayout={(lay) => console.debug('layout--', lay?.nativeEvent?.layout)}
        ref={(ref) => {
          tooltipV2?.setComponentReference?.(ref, 1);
          // Also assign to FTUE V2 ref if provided
          if (this.props.addCashButtonRef && ref) {
            (this.props.addCashButtonRef as any).current = ref;
          }
        }}
        style={{alignSelf: 'center'}}
      >
        <AddCashButton
          style={[styles.countingLabel, this._getTextStyle()]}
          containerStyle={
            this.props.style?.backgroundColor
              ? [
                  this.lightThemeBorder,
                  this.darkThemeBorder,
                  {backgroundColor: this.props.style?.backgroundColor},
                ]
              : [this.lightThemeBorder, this.darkThemeBorder]
          }
          cashLayout={(_e: any) => {
            /*RN Measure Workaround for Android*/
          }}
          value={I18n.message('Add Cash')}
          animationCurve={this.props.animationCurve}
          animationDuration={this.props.animationDuration}
          animationPrecision={this.props.animationPrecisions?.cash}
          shouldAnimate={this.props.shouldAnimate}
          identifier={this.props.identifier}
          forceAnimation={this.props.forceAnimation}
          onPressFunc={onPressFunction}
        />
      </View>,
      index
    );
  }

  _renderMedalBalance(index: number): React.ReactElement {
    return this._renderBalance(
      'medals',
      I18n.message('Medal balance {0}', this.props.medalBalance),
      'medal_balance',
      <MedalCountingLabel
        style={[styles.countingLabel, this._getTextStyle()]}
        containerStyle={
          this.props.style?.backgroundColor
            ? [
                this.lightThemeBorder,
                this.darkThemeBorder,
                {backgroundColor: this.props.style?.backgroundColor},
              ]
            : [this.lightThemeBorder, this.darkThemeBorder]
        }
        medalLayout={(_e: any) => {
          /*RN Measure Workaround for Android*/
        }}
        value={this.props.medalBalance}
        animationCurve={this.props.animationCurve}
        animationDuration={this.props.animationDuration}
        animationPrecision={this.props.animationPrecisions?.ticketz}
        medalRef={(ref: any) => {
          this.medalRef = ref;
        }}
        shouldAnimate={this.props.shouldAnimate}
        identifier={this.props.identifier}
        forceAnimation={this.props.forceAnimation}
      />,
      index
    );
  }

  _renderBalanceForType(type: string, index: number): React.ReactElement | boolean {
    switch (type) {
      case CurrencyTypes.CASH:
        return this._renderCashBalance(index);
      case CurrencyTypes.TICKETZ:
        return this._renderTicketzBalance(index);
      case CurrencyTypes.Z:
        return this._renderZBalance(index);
      case CurrencyTypes.MEDAL:
        return this._renderMedalBalance(index);
      default:
        return <View />;
    }
  }

  _onChatLayout(event: LayoutChangeEvent) {
    if (this.props.educationChatRef && this.props.onEducationChatSpotlightUpdated) {
      const node = findNodeHandle(this.props.educationChatRef.current);
      if (event?.nativeEvent?.layout && node) {
        RCTUIManager.measure(
          node,
          (
            _tileX: number,
            _tileY: number,
            _tileWidth: number,
            tileHeight: number,
            _tilePageX: number,
            _tilePageY: number
          ) => {
            const spotlightRefProps = {
              spotlightCornerRadiusChat: tileHeight,
              spotlightRefChat: node,
            };
            this.props.onEducationChatSpotlightUpdated(spotlightRefProps);
          }
        );
      }
    }
  }

  _renderChatIcon(): React.ReactElement {
    return (
      <View style={styles.chatContainer}>
        <TouchableOpacity testID={TEST_IDS.CHAT_BUTTON} onPress={this._onChatButtonPress}>
          <View ref={this.props.educationChatRef} onLayout={this._onChatLayout.bind(this)}>
            <Animated.View style={{transform: [{scale: this.scale}]}}>
              <SkillzSVG style={styles.chatIcon} source={chatIconSvg} />
            </Animated.View>
            <Optional test={this.props.showChatBadge}>
              <View style={styles.badge} testID={TEST_IDS.CHAT_BADGE} />
            </Optional>
          </View>
        </TouchableOpacity>
      </View>
    );
  }

  _renderSkillzPowered(): React.ReactElement {
    const tintColorForTheme = pickTextColorBasedOnBgColor(
      this.flattenedStyle.backgroundColor as string,
      '#FFFFFF',
      '#3A3A3A'
    );
    return (
      <SkillzImage
        style={styles.skillzPowered}
        resizeMode={'contain'}
        source={'ic_new_logo_skillz.svg'}
        tintColor={tintColorForTheme}
      />
    );
  }

  _renderRightSideIcon(): React.ReactElement | undefined {
    if (this.props.showRightSideIcon) {
      if (ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()) {
        return this._renderSkillzPowered();
      }
      return this._renderChatIcon();
    }
    return;
  }

  _viewTransactionPress() {
    this._removeAllTooltip();
    const gameId = SkillzModule.App.GameId;
    AWSManager.trackEvent(WALLET_TT_TRANSACTIONS_CLICK);
    trackTelemetryEvent({
      event: TelemetryEventName.TRANSACTIONS_PAGE_LOAD,
      includeUserWalletDetails: true,
    });
    // NavigationManager.popToRoot();
    SkillzSDK.resetToHomeScreen();
    SkillzRouter.push(`/games/${gameId}/account/balance`);
  }

  _viewWithdrawalPress() {
    this._removeAllTooltip();
    WithdrawalManager.launchWithdrawalFlow();
    AWSManager.trackEvent(WALLET_TT_WITHDRAW_CLICK);
  }

  _renderWithdrawBtnEligible() {
    const VC_ONLY = ClientPropertyConstants.getIsVirtualCurrencyOnly();
    const isWithdrawDisabled =
      VC_ONLY || !CurrentUserStore.isSavedAccount() || this.props.userInfo?.real_cash_balance <= 0;
    return (
      <>
        <View style={styles.dividerVertical} />
        <TouchableOpacity
          style={[styles.tooltipActionButton]}
          onPress={this._viewWithdrawalPress.bind(this)}
          disabled={isWithdrawDisabled}
        >
          <Label
            style={[styles.tootltipActionText, isWithdrawDisabled ? styles.withdrawDisabled : {}]}
          >
            {I18n.message('Withdraw')}
          </Label>
          {isWithdrawDisabled ? (
            <SkillzImage source={'ic_arrow_new_disabled.svg'} style={styles.iconsizeArrow} />
          ) : (
            <SkillzImage source={'ic_arrow_new_green.svg'} style={styles.iconsizeArrow} />
          )}
        </TouchableOpacity>
      </>
    );
  }

  _renderNewWalletBalanceTooltip(): React.ReactNode {
    const tooltipProps = {
      positionArrow: 'top-center',
      content: (
        <View style={styles.walletBalanceBreakdownBox}>
          {<BalanceSplitComponent userInfo={this.props.userInfo} removeBackground />}
          <View style={styles.divider} />
          <View style={styles.actionContainer}>
            <TouchableOpacity
              style={styles.tooltipActionButton}
              onPress={this._viewTransactionPress.bind(this)}
            >
              <Label style={styles.tootltipActionText}>{I18n.message('View Transactions')}</Label>
              <SkillzImage source={'ic_arrow_new_green.svg'} style={styles.iconsizeArrow} />
            </TouchableOpacity>
            {this._renderWithdrawBtnEligible()}
          </View>
        </View>
        // </Pressable>
      ),
      showRibbon: false,
      showSpinner: false,
      showV2Tooltip: false,
    };

    // Dynamic positioning based on orientation
    const tooltipPositionStyle =
      Sizes.isPortrait() && Sizes.isBrowser()
        ? styles.walletBalanceComponentPosition
        : {...styles.walletBalanceComponentPosition, left: '3%'};

    return (
      <Pressable
        style={[StyleSheet.absoluteFill, styles.overlayShade]}
        onPress={() => {
          this._removeAllTooltip();
        }}
      >
        <View style={styles.overlayContentBlocker}>
          <View style={[styles.tootltipContainer, tooltipPositionStyle]}>
            <TooltipView {...tooltipProps} />
          </View>
        </View>
      </Pressable>
    );
  }

  _renderZCashBalanceTooltip(): React.ReactNode {
    const tooltipProps = {
      positionArrow: 'top-start',
      content: (
        <>
          <View style={styles.zcashTooltipBox}>
            <SkillzImage
              source={SkillzStyle.currencyValues.practiceCurrencyFullIconLargeURI}
              resizeMode='contain'
              style={styles.zcashTooltipIcon}
            />
            <View>
              <CountingLabel
                identifier={'zBalance_tooltip' + this.props.identifier}
                value={this.props.zBalance}
                style={styles.zcashTooltipText}
                formatter={() => I18n.abbreviatedNumber(this.props.zBalance)}
                precision={0}
                countUpEnabled={false}
                forceAnimation={true}
              />
            </View>
          </View>
        </>
      ),
      showRibbon: false,
      showSpinner: false,
      showV2Tooltip: false,
    };
    return (
      <Pressable
        style={[StyleSheet.absoluteFill, styles.overlayShade]}
        onPress={() => {
          this._removeAllTooltip();
        }}
      >
        <View style={styles.overlayContentBlocker}>
          <View style={[styles.tootltipContainer]}>
            <TooltipView {...tooltipProps} />
          </View>
        </View>
      </Pressable>
    );
  }

  _renderNewWalletFeatureTooltip(): React.ReactNode {
    const tooltipProps = {
      positionArrow: 'top-center',
      content: (
        <>
          <View style={styles.tooltipBox}>
            <Label style={styles.tooltipWalletText} numberOfLines={0}>
              {I18n.message(
                'Tap to view your Cash and Bonus Cash balance. You can also access Transactions and Withdrawals from here.'
              )}
            </Label>
          </View>
        </>
      ),
      showRibbon: false,
      showSpinner: false,
      showV2Tooltip: false,
    };
    return (
      <Pressable
        style={[StyleSheet.absoluteFill, styles.overlayShade]}
        onPress={() => {
          this._removeAllTooltip();
          this.props.onDismissWalletTooltip?.();
          FTUEController.setHasSeenHomeScreenWalletTooltip(true);
        }}
      >
        <View style={styles.overlayContentBlocker}>
          <View style={[styles.tootltipContainer, styles.walletTooltipPosition]}>
            <TooltipView {...tooltipProps} />
          </View>
        </View>
      </Pressable>
    );
  }

  render(): React.ReactElement {
    const {disableNewFTUETooltip} = this.props;
    return (
      <FTUETooptipV2Context.Consumer>
        {(tooltipV2) => (
          <View
            testID={TEST_IDS.BALANCE_BAR}
            style={this.flattenedStyle}
            onLayout={this._updateBalanceViewLayout.bind(this)}
          >
            <View
              style={[
                styles.currencyContainer,
                this.props.isContentCentered ? {justifyContent: 'center'} : {},
              ]}
            >
              {this.props.currencyOrder.map(
                (type: string, index: number): React.ReactElement | boolean => {
                  return this._renderBalanceForType(type, index);
                }
              )}
              {this.props.showAddCashButton ? this._renderAddCashButton(4, tooltipV2) : null}
            </View>
            {this._renderRightSideIcon()}
            {this.state.showNewWalletTooltip ? this._renderNewWalletBalanceTooltip() : null}
            {this.state.showZTooltip ? this._renderZCashBalanceTooltip() : null}
            {!disableNewFTUETooltip && this.state.newFTUETooltip && NavigationManager.isSceneCurrentlyPresented(ScreenNames.ENTRY_SCREEN)
              ? this._renderNewWalletFeatureTooltip()
              : null}
          </View>
        )}
      </FTUETooptipV2Context.Consumer>
    );
  }
}

// comment added for recheck
